// codeblock-highlighter.js - Code block background highlighting for CodeMirror
// Highlights all lines between ``` and ``` with a continuous background

import { RangeSetBuilder } from "@codemirror/state";
import { Decoration, ViewPlugin } from "@codemirror/view";

// Code block background decorations - highlights all lines between ``` and ```
// Decorations are cursor-aware: fence lines get corners when cursor is inside code block
function createCodeBlockDecos(cursorInsideBlock) {
    return {
        fenceStart: Decoration.line({
            class: `cm-codeblock-line cm-codeblock-fence${cursorInsideBlock ? ' cm-codeblock-top-rounded' : ''}`
        }),
        fenceEnd: Decoration.line({
            class: `cm-codeblock-line cm-codeblock-fence${cursorInsideBlock ? ' cm-codeblock-bottom-rounded' : ''}`
        }),
        contentFirst: Decoration.line({
            class: `cm-codeblock-line cm-codeblock-content${!cursorInsideBlock ? ' cm-codeblock-top-rounded' : ''}`
        }),
        contentLast: Decoration.line({
            class: `cm-codeblock-line cm-codeblock-content${!cursorInsideBlock ? ' cm-codeblock-bottom-rounded' : ''}`
        }),
        contentOnly: Decoration.line({
            class: `cm-codeblock-line cm-codeblock-content${!cursorInsideBlock ? ' cm-codeblock-top-rounded cm-codeblock-bottom-rounded' : ''}`
        }),
        contentMiddle: Decoration.line({ class: "cm-codeblock-line cm-codeblock-content" })
    };
}

function findCodeBlocks(view) {
    const doc = view.state.doc;
    const text = doc.toString();
    const cursorPos = view.state.selection.main.head;
    const cursorLine = doc.lineAt(cursorPos).number;

    // Collect all decorations first
    const decorations = [];

    // Find all ``` markers (including indented ones)
    const regex = /^[ \t]*```.*$/gm;
    const markers = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
        markers.push(match.index);
    }

    // Pair up markers to find code block ranges
    for (let i = 0; i < markers.length - 1; i += 2) {
        const startOffset = markers[i];
        const endOffset = markers[i + 1];

        if (endOffset === undefined) break;

        const startLine = doc.lineAt(startOffset).number;
        const endLine = doc.lineAt(endOffset).number;
        const firstContentLine = startLine + 1;
        const lastContentLine = endLine - 1;

        // Check if cursor is inside this code block (including fence lines)
        const cursorInsideBlock = cursorLine >= startLine && cursorLine <= endLine;

        const decos = createCodeBlockDecos(cursorInsideBlock);

        for (let lineNum = startLine; lineNum <= endLine; lineNum++) {
            const line = doc.line(lineNum);
            let deco;

            if (lineNum === startLine) {
                deco = decos.fenceStart;
            } else if (lineNum === endLine) {
                deco = decos.fenceEnd;
            } else if (firstContentLine > lastContentLine) {
                continue;
            } else if (firstContentLine === lastContentLine && lineNum === firstContentLine) {
                deco = decos.contentOnly;
            } else if (lineNum === firstContentLine) {
                deco = decos.contentFirst;
            } else if (lineNum === lastContentLine) {
                deco = decos.contentLast;
            } else {
                deco = decos.contentMiddle;
            }

            decorations.push({ from: line.from, deco });
        }
    }

    // Sort by position and build the RangeSet
    decorations.sort((a, b) => a.from - b.from);

    const builder = new RangeSetBuilder();
    for (const { from, deco } of decorations) {
        builder.add(from, from, deco);
    }

    return builder.finish();
}

export const codeBlockHighlighter = ViewPlugin.fromClass(class {
    constructor(view) {
        this.decorations = findCodeBlocks(view);
    }

    update(update) {
        // Update on doc changes, viewport changes, or selection changes (cursor moved)
        if (update.docChanged || update.viewportChanged || update.selectionSet) {
            this.decorations = findCodeBlocks(update.view);
        }
    }
}, {
    decorations: v => v.decorations
});
